import { urlService, type LocationTarget } from '../url/url.service';
import { CustomHistoryState, normalizePath } from './customHistory';
import { useLocation } from 'react-router';

export interface UseReturnLocationOptions {
    exclude?: string | RegExp | (string | RegExp)[] | ((path: string) => boolean);
    onlyExplicit?: boolean;
}

export default function useReturnLocation({ exclude, onlyExplicit }: UseReturnLocationOptions = {}): LocationTarget | undefined {
    const location = useLocation<CustomHistoryState | undefined>();
    let returnLocation;

    if (typeof location.state?.returnLocation === 'object') {
        // If an explicit returnLocation was set for the current location, use it.
        returnLocation = location.state.returnLocation;
    } else if (!onlyExplicit) {
        // Find first occurrence of an excluded path in the returnLocationStack and ignore everything after it, including that path.
        // E.g. if the stack is ["/", "/route-a/", "/route-a/tab-1/", "/route-a/tab-2/"] and exclude is "/route-a/", the user could still
        // return to "/". Switching to another tab when pressing the back button would be confusing.
        // If no path is excluded, the whole stack is considered valid and the user can return to the last location.
        const predicate = createPredicate(exclude);
        const index = (location?.state?.returnLocationStack ?? []).findIndex((entry) => predicate(entry.normalizedPath));
        returnLocation = location.state?.returnLocationStack?.slice(0, index >= 0 ? index : undefined).at(-1)?.location;
    }

    return (
        returnLocation && {
            ...urlService.locationToTarget(returnLocation),
            replaceSearch: true,
            replaceHash: true,
        }
    );
}

function createPredicate(exclude: UseReturnLocationOptions['exclude']): (path: string) => boolean {
    if (typeof exclude === 'string') {
        const prefix = normalizePath(exclude);
        return (path) => path.startsWith(prefix);
    }

    if (exclude instanceof RegExp) {
        return (path) => {
            exclude.lastIndex = 0;
            return exclude.test(path);
        };
    }

    if (Array.isArray(exclude)) {
        const predicates = exclude.map(createPredicate);
        return (path) => predicates.some((predicate) => predicate(path));
    }

    if (typeof exclude === 'function') {
        return exclude;
    }

    return () => false;
}
